import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ArrowRightIcon, CheckCircleIcon, ClockIcon, MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline'
import { useToast } from '../contexts/ToastContext'
import { supabase } from '../lib/supabase'
import { useProductLines, type LinePreview, type ProductLine } from '../hooks/useProductLines'
import ProviderConnectModal from './ProviderConnectModal'
import { useSupplierProfiles } from '../hooks/useSupplierProfiles'

/**
 * Onboarding step "Products & suppliers": a loop, one product line at a time.
 *
 *   search a brand or model (preview across EVERY supplier) -> select it as a product line
 *   -> the suppliers that carry THAT line, each with a Connect (connection request)
 *   -> "Connect another product line" (back to search) or "Done" (next step)
 *
 * A merchant does not know what a supplier stocks, so suppliers are only ever offered for
 * products the merchant just picked. "Sell everything" is still available as a way out.
 */

export interface SupplierRow { id: string; name: string; provider_type: string | null }
export type SellMode = 'lines' | 'everything'

interface Props {
  merchantId: string
  userId: string
  isNewMerchant: boolean
  providers: SupplierRow[]
  connected: Set<string>
  pending: Set<string>
  reloadSuppliers: () => void
  sellMode: SellMode
  setSellMode: (m: SellMode) => void
  onLinesChange: (lines: ProductLine[]) => void
  onBack: () => void
  onDone: () => void
}

type Focus = { id: string; name: string; perProvider: LinePreview['per_provider'] } | null

const primaryBtn = 'inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-orange-500 hover:bg-orange-400 text-black font-semibold text-sm disabled:opacity-50 transition-colors'
const ghostBtn = 'inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/20 text-white/70 hover:text-white text-sm transition-colors disabled:opacity-50'
const input = 'glass-input w-full'

async function setModeFor(merchantId: string, mode: 'deselect_all' | 'select_all') {
  const { data, error } = await supabase.functions.invoke('merchant-product-lines', {
    body: { shop_id: merchantId, action: 'set_mode', mode, include_future: true },
  })
  if (error || data?.error) throw new Error(data?.error || error?.message || 'Could not save what you want to sell')
}

const OnboardingProductsAndSuppliers: React.FC<Props> = ({
  merchantId, userId, isNewMerchant, providers, connected, pending, reloadSuppliers,
  sellMode, setSellMode, onLinesChange, onBack, onDone,
}) => {
  const toast = useToast()
  const pl = useProductLines(merchantId, { catalog: true })
  const [phase, setPhase] = useState<'search' | 'suppliers' | null>(null)
  const [focus, setFocus] = useState<Focus>(null)
  const [brand, setBrand] = useState('')
  const [model, setModel] = useState('')
  const [preview, setPreview] = useState<LinePreview | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [finishing, setFinishing] = useState(false)
  // Supplier-declared terms, so the connect dialog shows shipping time and cost here too.
  const { profiles: supplierProfiles } = useSupplierProfiles()
  const [connecting, setConnecting] = useState<SupplierRow | null>(null)
  const seq = useRef(0)

  const { loaded, lines, brands, loadBrands } = pl
  useEffect(() => { onLinesChange(lines) }, [lines, onLinesChange])
  // First visit: straight to search. Coming back with lines already chosen: their suppliers.
  useEffect(() => {
    if (loaded && phase === null) setPhase(lines.length || sellMode === 'everything' ? 'suppliers' : 'search')
  }, [loaded, lines.length, phase, sellMode])
  useEffect(() => { if (brands.length === 0) loadBrands().catch(() => { /* typing still works */ }) }, [brands.length, loadBrands])

  const brandKey = useMemo(() => {
    const v = brand.trim()
    if (!v) return null
    const hit = brands.find((b) => b.label.toLowerCase() === v.toLowerCase() || b.brand === v.toLowerCase())
    return hit?.brand ?? v
  }, [brand, brands])
  const brandLabel = (key: string) => brands.find((b) => b.brand === key)?.label ?? key
  const patterns = model.trim() ? [model.trim()] : []
  const lineName = [brandKey ? brandLabel(brandKey) : '', model.trim()].filter(Boolean).join(' ')

  // Live search across every supplier, debounced; stale answers are dropped.
  const { preview: runPreview } = pl
  useEffect(() => {
    if (phase !== 'search' || (!brandKey && patterns.length === 0)) { setPreview(null); setPreviewError(null); return }
    const n = ++seq.current
    setPreviewing(true)
    const t = setTimeout(async () => {
      try {
        const r = await runPreview({ name: lineName || 'preview', tag: lineName || 'preview', brand: brandKey, patterns, exclude_patterns: [], footwear_only: true, provider_ids: null })
        if (n === seq.current) { setPreview(r); setPreviewError(null) }
      } catch (e: any) {
        if (n === seq.current) setPreviewError(e.message)
      } finally {
        if (n === seq.current) setPreviewing(false)
      }
    }, 600)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, brandKey, model, runPreview])

  const providerName = useMemo(() => new Map(providers.map((p) => [p.id, p.name])), [providers])
  const lineNames = useMemo(() => new Map(lines.map((l) => [l.id, l.name])), [lines])

  const selectLine = async () => {
    if (!preview || !lineName) return
    setSaving(true)
    try {
      const existing = lines.find((l) => l.name.toLowerCase() === lineName.toLowerCase())
      const lineId = existing?.id ?? (await pl.save({
        name: lineName, tag: lineName, brand: brandKey, patterns, exclude_patterns: [],
        footwear_only: true, provider_ids: null, create_collection: true,
      })).line_id
      // From the first line on, every supplier (including ones approved later) sells only
      // the merchant's lines. A store set up before keeps its current setting.
      if (isNewMerchant) await setModeFor(merchantId, 'deselect_all')
      setFocus({ id: lineId, name: lineName, perProvider: preview.per_provider })
      setPhase('suppliers')
    } catch (e: any) {
      toast.error(e.message)
    } finally {
      setSaving(false)
    }
  }

  const searchAgain = () => {
    setBrand(''); setModel(''); setPreview(null); setFocus(null); setPhase('search')
  }

  const done = async () => {
    setFinishing(true)
    try {
      if (isNewMerchant && sellMode === 'everything') await setModeFor(merchantId, 'select_all')
      onDone()
    } catch (e: any) {
      toast.error(e.message)
    } finally {
      setFinishing(false)
    }
  }

  // Suppliers shown: for the line just picked, the suppliers carrying it; on return, the
  // suppliers carrying any of the merchant's lines; in "everything" mode, all of them.
  const supplierList: Array<{ id: string; products?: number; detail?: string }> = sellMode === 'everything'
    ? providers.map((p) => ({ id: p.id }))
    : focus
      ? focus.perProvider.map((x) => ({ id: x.provider_id, products: x.products, detail: focus.name }))
      : (pl.counts?.suppliers ?? []).map((x) => ({
        id: x.provider_id, products: x.products,
        detail: x.lines.map((id) => lineNames.get(id)).filter(Boolean).join(' · '),
      }))
  const requested = providers.filter((p) => connected.has(p.id) || pending.has(p.id))

  if (!loaded || phase === null) return <p className="text-white/40 text-sm">Loading…</p>

  return (
    <div>
      {/* Lines chosen so far */}
      {sellMode === 'lines' && lines.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <span className="text-xs text-white/40">Your product lines:</span>
          {lines.map((l) => (
            <span key={l.id} className={`text-xs px-2.5 py-1 rounded-full border ${focus?.id === l.id ? 'border-orange-500 text-white' : 'border-white/15 text-white/70'}`}>{l.name}</span>
          ))}
        </div>
      )}

      {sellMode === 'lines' && phase === 'search' && (
        <>
          <h3 className="font-heading font-bold text-white text-xl mb-1">{lines.length ? 'Search for another product line' : 'What do you want to sell?'}</h3>
          <p className="text-white/40 text-sm mb-4">Search a brand, or a brand and model. Results come from every supplier on FlowStock.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-white/50" htmlFor="pl-brand">Brand</label>
              <input id="pl-brand" list="pl-brands" className={input} value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="e.g. UGG, Nike, Adidas" autoFocus />
              <datalist id="pl-brands">{brands.map((b) => <option key={b.brand} value={b.label}>{b.products} products</option>)}</datalist>
            </div>
            <div>
              <label className="text-xs text-white/50" htmlFor="pl-model">Model (optional)</label>
              <input id="pl-model" className={input} value={model} onChange={(e) => setModel(e.target.value)} placeholder="e.g. Air Max 1 · empty for the whole brand" />
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-white/10 p-3 min-h-[5rem]">
            {!brandKey && patterns.length === 0 ? (
              <p className="text-white/40 text-sm flex items-center gap-2"><MagnifyingGlassIcon className="w-4 h-4" /> Type a brand to see what suppliers have.</p>
            ) : previewError ? (
              <p className="text-red-400 text-sm">{previewError}</p>
            ) : !preview ? (
              <p className="text-white/40 text-sm">Searching…</p>
            ) : (
              <div className={previewing ? 'opacity-60' : ''}>
                <p className="text-white text-sm mb-2">
                  <span className="font-semibold">{preview.products}</span> products at{' '}
                  <span className="font-semibold">{preview.per_provider.length}</span> supplier{preview.per_provider.length === 1 ? '' : 's'}
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {preview.sample.map((s) => (
                    <div key={s.sku} className="text-[10px] text-white/60" title={s.title}>
                      {s.photo_url
                        ? <img src={s.photo_url} alt="" className="w-full aspect-square object-cover rounded bg-white/5" onError={(e) => { e.currentTarget.style.visibility = 'hidden' }} />
                        : <div className="w-full aspect-square rounded bg-white/5" />}
                      <div className="truncate mt-1">{s.title}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-wrap justify-between gap-3 mt-6">
            {lines.length
              ? <button type="button" className={ghostBtn} onClick={() => { setFocus(null); setPhase('suppliers') }}>Cancel</button>
              : <button type="button" className={ghostBtn} onClick={onBack}>Back</button>}
            <button type="button" className={primaryBtn} disabled={saving || previewing || !preview || preview.products === 0} onClick={selectLine}>
              {saving ? 'Saving…' : `Select ${lineName || 'this product line'}`} <ArrowRightIcon className="w-4 h-4" />
            </button>
          </div>
          <button type="button" className="mt-4 text-xs text-white/40 hover:text-white underline" onClick={() => { setSellMode('everything'); setPhase('suppliers') }}>
            Or sell everything your suppliers carry
          </button>
        </>
      )}

      {phase === 'suppliers' && (
        <>
          <h3 className="font-heading font-bold text-white text-xl mb-1">
            {sellMode === 'everything' ? 'Choose your suppliers' : focus ? `Suppliers with ${focus.name}` : 'Suppliers for your product lines'}
          </h3>
          <p className="text-white/40 text-sm mb-5">
            Connect sends a request to the supplier. Their stock appears in your store once they approve; you'll get an email.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {supplierList.map((s) => {
              const p = providers.find((x) => x.id === s.id)
              const state = connected.has(s.id) ? 'connected' : pending.has(s.id) ? 'pending' : 'none'
              return (
                <div key={s.id} className="glass-card rounded-xl p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-white font-semibold text-sm truncate">{providerName.get(s.id) ?? 'Supplier'}</p>
                    {s.products !== undefined && <p className="text-xs text-white/70">{s.products} product{s.products === 1 ? '' : 's'}{focus ? '' : ' of yours'}</p>}
                    {!focus && s.detail && <p className="text-xs text-white/40">{s.detail}</p>}
                    {state === 'connected' && <p className="text-xs text-green-400 flex items-center gap-1"><CheckCircleIcon className="w-3.5 h-3.5" /> Connected</p>}
                    {state === 'pending' && <p className="text-xs text-yellow-300/80 flex items-center gap-1"><ClockIcon className="w-3.5 h-3.5" /> Waiting for approval</p>}
                  </div>
                  {state === 'none' && p && (
                    <button type="button" className="px-3 py-1.5 rounded-full border border-orange-500/40 text-orange-300 hover:bg-orange-500/10 text-xs font-semibold shrink-0" onClick={() => setConnecting(p)}>Connect</button>
                  )}
                </div>
              )
            })}
          </div>
          {supplierList.length === 0 && (
            <p className="text-sm text-white/50">No supplier carries this yet. It stays in your product lines, and products appear as soon as a supplier you connect has them.</p>
          )}
          {requested.length === 0 && (
            <p className="text-xs text-yellow-300/70 mt-4">Connect at least one supplier, or you'll have nothing to sell. You can also add suppliers later.</p>
          )}

          <div className="flex flex-wrap justify-between gap-3 mt-6">
            {sellMode === 'everything'
              ? <button type="button" className={ghostBtn} onClick={() => { setSellMode('lines'); setPhase(lines.length ? 'suppliers' : 'search') }}>Back to product lines</button>
              : <button type="button" className={ghostBtn} onClick={searchAgain}><PlusIcon className="w-4 h-4" /> Connect another product line</button>}
            <button type="button" className={primaryBtn} disabled={finishing} onClick={done}>
              {finishing ? 'Saving…' : 'Done'} <ArrowRightIcon className="w-4 h-4" />
            </button>
          </div>
        </>
      )}

      {connecting && (
        <ProviderConnectModal
          providerId={connecting.id}
          providerName={connecting.name}
          providerType={connecting.provider_type ?? ''}
          merchantId={merchantId}
          authUserId={userId}
          profile={supplierProfiles[connecting.id]}
          onClose={() => setConnecting(null)}
          onSuccess={() => { setConnecting(null); reloadSuppliers() }}
        />
      )}
    </div>
  )
}

export default OnboardingProductsAndSuppliers
